import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTemplates } from '../services/api';
import { formatDate, StatusBadge } from '../utils/helpers';
import { BookOpen, ChevronRight, Copy } from 'lucide-react';

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    getTemplates(filterType ? { type: filterType } : {})
      .then(r => setTemplates(r.data.templates || []))
      .finally(() => setLoading(false));
  }, [filterType]);

  const termTypes = ['', 'DELIVERY_BASED', 'MILESTONE_BASED', 'PERIOD_TRANCHE', 'SUBSCRIPTION', 'HYBRID'];

  // Predefined quick-start templates based on spec types A-I
  const quickTemplates = [
    { code: 'A', name: '100% on Delivery', desc: 'Full SO value on delivery acceptance', trigger: 'DELIVERY_ACCEPTANCE', installments: 1 },
    { code: 'B', name: 'Advance + Delivery', desc: 'Configurable split: X% on SO, balance on delivery', trigger: 'SO + DELIVERY', installments: 2 },
    { code: 'C', name: 'Advance + Delivery + Retention', desc: '3-tranche: advance, delivery, retention hold', trigger: 'SO + DELIVERY + TIME', installments: 3 },
    { code: 'D', name: '100% Deferred', desc: 'Full amount deferred after delivery acceptance', trigger: 'TIME_PERIOD', installments: 1 },
    { code: 'E', name: 'Milestone Split', desc: 'N milestones with configurable payment % each', trigger: 'MILESTONE_ACCEPTANCE', installments: 'N' },
    { code: 'F', name: 'Time-Period Tranche', desc: 'N tranches spread over time after delivery', trigger: 'TIME_PERIOD × N', installments: 'N' },
    { code: 'G', name: 'Auto-Renew Subscription', desc: 'Annual billing with YoY increment, auto-renews', trigger: 'SUBSCRIPTION_CYCLE', installments: 'Recurring' },
    { code: 'H', name: 'Buyer-Approval Subscription', desc: 'Subscription requiring buyer approval each cycle', trigger: 'SUBSCRIPTION_CYCLE', installments: 'Recurring' },
    { code: 'I', name: 'Multi-Year Fixed Contract', desc: 'Pre-agreed pricing per year, manual renewal', trigger: 'SUBSCRIPTION_CYCLE', installments: 'N/year' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Payment Term Templates</h2>
        <button onClick={() => navigate('/payment-terms')} className="btn-primary">
          <BookOpen size={16} /> Create Template
        </button>
      </div>

      {/* Spec Reference Card */}
      <div className="card p-5">
        <h3 className="font-semibold mb-4 text-gray-800">Quick Reference — All Term Types (A–I)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {quickTemplates.map(t => (
            <div key={t.code} className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:border-blue-200 hover:bg-blue-50 transition-colors cursor-pointer"
              onClick={() => navigate('/payment-terms')}>
              <div className="w-8 h-8 bg-blue-600 text-white rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0">{t.code}</div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">{t.name}</div>
                <div className="text-xs text-gray-500 mt-0.5">{t.desc}</div>
                <div className="flex items-center gap-3 mt-1.5">
                  <span className="text-xs text-blue-600 font-medium">{t.trigger}</span>
                  <span className="text-xs text-gray-400">{t.installments} installment{t.installments !== 1 ? 's' : ''}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Saved Templates */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Saved Templates</h3>
          <select className="select max-w-xs text-sm" value={filterType} onChange={e => setFilterType(e.target.value)}>
            {termTypes.map(t => <option key={t} value={t}>{t || 'All Types'}</option>)}
          </select>
        </div>

        <div className="card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr>{['Name','Type','Category','Created By','Created',''].map(h => <th key={h} className="table-th">{h}</th>)}</tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="table-td text-center text-gray-400">Loading…</td></tr>
              ) : templates.length === 0 ? (
                <tr><td colSpan={6} className="table-td text-center">
                  <div className="py-8 text-gray-400">
                    <BookOpen size={32} className="mx-auto mb-2 opacity-40" />
                    <p>No templates saved yet</p>
                    <p className="text-xs mt-1">Create a payment term and check "Save as template"</p>
                  </div>
                </td></tr>
              ) : templates.map(t => (
                <tr key={t.term_id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/payment-terms/${t.term_id}`)}>
                  <td className="table-td font-medium">{t.name}</td>
                  <td className="table-td text-xs text-gray-500">{t.term_type?.replace(/_/g,' ')}</td>
                  <td className="table-td text-xs text-gray-500">{t.procurement_category || '—'}</td>
                  <td className="table-td text-gray-500">{t.created_by_name}</td>
                  <td className="table-td text-gray-500">{formatDate(t.created_at)}</td>
                  <td className="table-td">
                    <button className="text-gray-400 hover:text-blue-600" title="Clone template"><Copy size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
